import { useId, useState, type InputHTMLAttributes } from "react";
import { AlertOctagon, Check, Eye, EyeOff } from "lucide-react";

import { passwordStrength } from "@/lib/validators";

type FieldProps = {
  label: string;
  required?: boolean;
  error?: string;
  valid?: boolean;
  hint?: string;
} & Omit<InputHTMLAttributes<HTMLInputElement>, "id">;

export function Field({
  label,
  required,
  error,
  valid,
  hint,
  ...rest
}: FieldProps) {
  const id = useId();
  return (
    <div className={"fld" + (error ? " fld--err" : "")}>
      <label htmlFor={id} className="fld__lbl">
        {label}
        {required && <span className="fld__req"> *</span>}
      </label>
      <div className="fld__wrap">
        <input id={id} className="fld__input" {...rest} />
        {valid && !error && (
          <span className="fld__ok">
            <Check size={16} strokeWidth={2.2} />
          </span>
        )}
      </div>
      {error && (
        <div className="fld__err">
          <AlertOctagon size={14} strokeWidth={1.8} />
          {error}
        </div>
      )}
      {!error && hint && <div className="fld__hint">{hint}</div>}
    </div>
  );
}

export function PasswordField({
  label = "Contraseña",
  required = true,
  value,
  onChange,
  onBlur,
  error,
}: {
  label?: string;
  required?: boolean;
  value: string;
  onChange: (v: string) => void;
  onBlur?: () => void;
  error?: string;
}) {
  const id = useId();
  const [show, setShow] = useState(false);
  const strength = value ? passwordStrength(value) : null;
  return (
    <div className={"fld" + (error ? " fld--err" : "")}>
      <label htmlFor={id} className="fld__lbl">
        {label}
        {required && <span className="fld__req"> *</span>}
      </label>
      <div className="fld__wrap">
        <input
          id={id}
          className="fld__input"
          type={show ? "text" : "password"}
          autoComplete="new-password"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
        />
        <button
          type="button"
          className="fld__eye"
          onClick={() => setShow((v) => !v)}
          aria-label={show ? "Ocultar contraseña" : "Mostrar contraseña"}
        >
          {show ? (
            <EyeOff size={18} strokeWidth={1.6} />
          ) : (
            <Eye size={18} strokeWidth={1.6} />
          )}
        </button>
      </div>
      {strength && (
        <div className={"strength strength--" + strength.score}>
          <span className="strength__bar"></span>
          <span className="strength__lbl">{strength.label}</span>
        </div>
      )}
      {error && (
        <div className="fld__err">
          <AlertOctagon size={14} strokeWidth={1.8} />
          {error}
        </div>
      )}
    </div>
  );
}
